import React, {useEffect, useRef, useState} from "react";
import {
    Bars3Icon,
    BookmarkIcon,
    BookOpenIcon,
    ChevronDownIcon,
    EyeIcon,
    PencilIcon,
    ShareIcon
} from "@heroicons/react/24/outline";
import {BookmarkIcon as BookmarkSolid,} from "@heroicons/react/24/solid";
import useZustandStore from "../hooks/useZustandStore";

export default React.memo(function Topbar({
                                              focusMode, onToggleFocus,
                                              fontSize, onFontSize,
                                              bookmarked, onToggleBookmark,
                                              onOpenSidebar, onOpenPanel,
                                              chapters, activeChapterId, onChapterSelect,
                                          }) {
    const [tocOpen, setTocOpen] = useState(false);
    const tocRef = useRef(null);

    // 点击外部关闭
    useEffect(() => {
        const handler = (e) => {
            if (tocRef.current && !tocRef.current.contains(e.target)) {
                setTocOpen(false);
            }
        };
        if (tocOpen) {
            document.addEventListener("mousedown", handler);
            return () => document.removeEventListener("mousedown", handler);
        }
    }, [tocOpen]);

    const activeBook = useZustandStore((state) => state.activeBook)
    const chapterTitle = useZustandStore((state) => state.chapterTitle)
    const chapterOrd = useZustandStore((state) => state.chapterOrd)
    return (
        <div
            className={`
        flex items-center px-3 md:px-7 h-12 md:h-14 flex-shrink-0 relative z-10
        border-b border-paper-deep bg-paper/95 backdrop-blur-md
        transition-opacity duration-500 gap-1 md:gap-0
      `}
            style={{opacity: focusMode ? 0.15 : 1}}
        >
            {/* ── 移动端：汉堡菜单 ── */}
            <button
                onClick={onOpenSidebar}
                className="md:hidden p-2 -ml-1 text-ink-faint hover:text-ink"
                aria-label="打开目录"
            >
                <Bars3Icon className="w-5 h-5"/>
            </button>

            {/* Book / chapter title — 点击弹出目录下拉 */}
            <div className="flex items-baseline gap-2 md:gap-3 flex-1 min-w-0" ref={tocRef}>
                <button
                    onClick={() => setTocOpen(v => !v)}
                    className="flex items-baseline gap-2 md:gap-3 min-w-0 group cursor-pointer"
                    title="章节目录"
                >
                    <span
                        className="font-display text-base md:text-xl text-ink tracking-[2px] md:tracking-[4px] truncate group-hover:text-vermilion transition-colors">
                        {activeBook}
                    </span>
                    <span
                        className="hidden sm:inline text-[11px] md:text-[13px] text-ink-faint tracking-[1px] md:tracking-[2px] truncate">
                        {chapterTitle || '—'} · 第{chapterOrd + 1}节
                    </span>
                    <ChevronDownIcon
                        className={`w-3.5 h-3.5 text-ink-faint flex-shrink-0 transition-transform duration-200 ${tocOpen ? 'rotate-180' : ''}`}/>
                </button>

                {/* ── 目录下拉面板 ── */}
                {tocOpen && (
                    <div
                        className="absolute top-full left-0 md:left-auto mt-1.5 w-64 max-h-72 overflow-y-auto scrollbar-thin-paper bg-paper border border-paper-deep rounded-lg shadow-xl z-50 py-1">
                        <div className="px-4 py-2 border-b border-paper-deep">
                            <p className="text-[10px] text-ink-faint tracking-[3px] font-serif">章 节 目 录</p>
                        </div>
                        {(!chapters || chapters.length === 0) ? (
                            <p className="text-[12px] text-ink-faint/50 text-center py-6 font-serif tracking-[2px]">
                                暂无章节
                            </p>
                        ) : (
                            chapters.map((ch, idx) => (
                                <button
                                    key={ch.id}
                                    onClick={() => {
                                        onChapterSelect(ch.id);
                                        setTocOpen(false);
                                    }}
                                    className={`
                                        w-full flex items-center gap-3 px-4 py-2.5 text-left font-serif transition-colors
                                        ${activeChapterId === ch.id
                                        ? "bg-vermilion/8 border-l-2 border-vermilion"
                                        : "border-l-2 border-transparent hover:bg-paper-deep"
                                    }
                                    `}
                                >
                                    <span className="text-[10px] text-ink-faint w-6 tracking-wide flex-shrink-0">
                                        {ch.ord + 1}
                                    </span>
                                    <span
                                        className={`text-[13px] tracking-wide truncate ${activeChapterId === ch.id ? "text-vermilion" : "text-ink-light"}`}>
                                        {ch.title}
                                    </span>
                                </button>
                            ))
                        )}
                    </div>
                )}
            </div>

            {/* Font size control */}
            <div className="hidden sm:flex items-center border border-paper-deep rounded overflow-hidden bg-paper-warm">
                <TopBtn onClick={() => onFontSize(-1)} label="A⁻"/>
                <span
                    className="text-[11px] text-ink-faint min-w-[24px] md:min-w-[28px] text-center tracking-wide select-none">
                    {fontSize}
                </span>
                <TopBtn onClick={() => onFontSize(1)} label="A⁺"/>
            </div>

            <Divider className="hidden sm:block"/>

            <IconBtn
                onClick={onToggleBookmark}
                title="书签"
                active={bookmarked}
                icon={bookmarked
                    ? <BookmarkSolid className="w-[16px] h-[16px] md:w-[18px] md:h-[18px]"/>
                    : <BookmarkIcon className="w-[16px] h-[16px] md:w-[18px] md:h-[18px]"/>
                }
            />
            <IconBtn title="批注" icon={<PencilIcon className="hidden sm:block w-[18px] h-[18px]"/>}/>
            <IconBtn title="分享" icon={<ShareIcon className="hidden sm:block w-[18px] h-[18px]"/>}/>

            <Divider className="hidden sm:block"/>

            <IconBtn
                onClick={onToggleFocus}
                title="专注模式"
                active={focusMode}
                icon={<EyeIcon className="w-[16px] h-[16px] md:w-[18px] md:h-[18px]"/>}
            />

            {/* ── 移动端：右侧面板按钮 ── */}
            <button
                onClick={onOpenPanel}
                className="md:hidden p-2 -mr-1 text-ink-faint hover:text-ink"
                aria-label="打开面板"
            >
                <BookOpenIcon className="w-5 h-5"/>
            </button>
        </div>
    );
});

function TopBtn({onClick, label}) {
    return (
        <button
            onClick={onClick}
            className="px-2.5 py-1 font-serif text-[13px] text-ink-faint hover:bg-paper-deep hover:text-ink transition-colors"
        >
            {label}
        </button>
    );
}

function IconBtn({icon, title, active, onClick}) {
    return (
        <button
            onClick={onClick}
            title={title}
            className={`
        w-[34px] h-[34px] flex items-center justify-center rounded-md
        transition-all duration-150
        ${active
                ? "bg-vermilion/10 text-vermilion"
                : "text-ink-faint hover:bg-paper-deep hover:text-ink"
            }
      `}
        >
            {icon}
        </button>
    );
}

function Divider({className}) {
    return <div className={`w-px h-5 bg-paper-deep mx-1 ${className || ''}`}/>;
}
